import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from "react";
import { useSelector, RootStateOrAny } from "react-redux";
import { Box, Button, HStack, Text, Pressable, VStack } from "native-base";
import { Animated } from "react-native";
import {
  MicIcon,
  StopIcon,
  PlayIcon,
  PauseIcon,
} from "../../assets/icons/icon-assets";
import { StyleSheet } from "react-native";

// Audio Manager to handle multiple audio playback
class AudioManager {
  private static instance: AudioManager;
  private currentAudio: HTMLAudioElement | null = null;
  private onAudioSwitch: (() => void) | null = null;

  private constructor() {}

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  setCurrentAudio(audio: HTMLAudioElement | null, onSwitch: () => void) {
    if (this.currentAudio && this.currentAudio !== audio) {
      this.currentAudio.pause();
      if (this.onAudioSwitch) {
        this.onAudioSwitch();
      }
    }
    this.currentAudio = audio;
    this.onAudioSwitch = onSwitch;
  }

  stopCurrent(onStop: () => void) {
    if (this.currentAudio) {
      this.currentAudio.pause();
      if (this.onAudioSwitch) {
        this.onAudioSwitch();
      }
      this.currentAudio = null;
      this.onAudioSwitch = null;
      onStop();
    }
  }
}

interface VoiceMessageProps {
  onRecordingComplete: (blob: Blob) => void;
  isRecording?: boolean;
  setIsRecording?: (value: boolean) => void;
  audioUrl?: string;
  isDisabled?: boolean;
  isNewVoiceChat?: boolean;
  hasAudio?: boolean;
  recordingAudioTime?: number;
}

export interface VoiceMessageHandle {
  reset: () => void;
}

const VoiceMessage: ForwardRefRenderFunction<
  VoiceMessageHandle,
  VoiceMessageProps
> = (
  {
    onRecordingComplete,
    isRecording = false,
    setIsRecording,
    audioUrl,
    isDisabled = false,
    isNewVoiceChat = false,
    hasAudio,
    recordingAudioTime,
  },
  ref,
) => {
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressTimerRef = useRef<number | null>(null);
  const skipOnStopRef = useRef<boolean>(false);
  const audioManager = useRef<AudioManager>(AudioManager.getInstance());

  const isOnline = useSelector(
    (state: RootStateOrAny) => state.user.isOnline
  );

  // Add these new state variables
  const [waveformBars, setWaveformBars] = useState<number[]>(
    Array.from({ length: 50 }, () => Math.random() * 20 + 10),
  );
  const barScales = useRef(
    Array.from({ length: 90 }, () => new Animated.Value(0.5)),
  ).current;

  // Generate waveform function
  const generateWaveform = useCallback(() => {
    setWaveformBars(Array.from({ length: 22 }, () => Math.random() * 20 + 10));
  }, []);

  // Animate bars when playing or recording
  const animateWaveform = React.useCallback(() => {
    const animations = barScales.map((scale) => {
      const randomDuration = Math.random() * 700 + 500;
      return Animated.loop(
        Animated.sequence([
          Animated.timing(scale, {
            toValue: Math.random() * 0.8 + 0.6,
            duration: randomDuration,
            useNativeDriver: true,
          }),
          Animated.timing(scale, {
            toValue: 0.4,
            duration: randomDuration,
            useNativeDriver: true,
          }),
        ]),
      );
    });

    Animated.parallel(animations).start();
  }, [barScales]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      stopTimer();
      setIsRecording?.(false);
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(0);
      setRecordingTime(0);
      skipOnStopRef.current = true;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (mediaRecorderRef.current?.state === "recording") {
        const tracks = (mediaRecorderRef.current as any).stream?.getTracks?.();
        if (tracks && tracks.length > 0) {
          tracks.forEach((track: MediaStreamTrack) => track.stop());
        }
        mediaRecorderRef.current.stop();
      }
    },
  }));

  React.useEffect(() => {
    if (isPlaying || isRecording) {
      animateWaveform();
    } else {
      barScales.forEach((scale) => {
        scale.setValue(0.5);
        scale.stopAnimation();
      });
    }
  }, [isPlaying, isRecording, generateWaveform]);

  const startRecording = async () => {
    try {
      skipOnStopRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (skipOnStopRef.current) {
          skipOnStopRef.current = false;
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        onRecordingComplete(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording?.(true);
      startTimer();
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording?.(false);
      stopTimer();
      setRecordingTime(0);
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Load audio metadata when component mounts
  useEffect(() => {
    let audio: HTMLAudioElement | null = null;

    const loadAudio = async () => {
      if (audioUrl) {
        audio = new Audio(audioUrl);
        audio.preload = "metadata";

        try {
          await new Promise((resolve, reject) => {
            const handleMetadata = () => {
              const audioDuration = audio?.duration;
              if (audioDuration && isFinite(audioDuration)) {
                setDuration(audioDuration);
              }
              resolve(undefined);
            };

            const handleError = (error: Event) => {
              reject(error);
            };

            audio?.addEventListener("loadedmetadata", handleMetadata, {
              once: true,
            });
            audio?.addEventListener("error", handleError, { once: true });

            if (audio) {
              audio.load();
            }
          });
        } catch (error) {
          console.error("Error loading audio metadata:", error);
        }
      }
    };

    loadAudio();

    return () => {
      if (audio) {
        audio.removeEventListener("loadedmetadata", () => {});
        audio.removeEventListener("error", () => {});
        audio.remove();
      }
    };
  }, [audioUrl]);

  // Create audio instance for playback
  useEffect(() => {
    let isMounted = true;

    if (audioUrl && !audioRef.current) {
      const audio = new Audio(audioUrl);
      audio.preload = "metadata";
      audioRef.current = audio;

      const handleMetadata = () => {
        const audioDuration = audio.duration;
        if (audioDuration && isFinite(audioDuration)) {
          if (isMounted) setDuration(audioDuration);
        } else if (audio.duration === Infinity) {
          // Fix duration
          audio.currentTime = 1e101; // Seek far into the future
          audio.ontimeupdate = () => {
            audio.ontimeupdate = null;
            audio.currentTime = 0;
            if (isMounted) {
              console.log("Fixed Duration:", audio.duration);
              setDuration(audio.duration);
            }
          };
        }
      };

      audio.addEventListener("loadedmetadata", handleMetadata, { once: true });
      audio.load();

      return () => {
        isMounted = false;
        audio.removeEventListener("loadedmetadata", handleMetadata);
        if (audioRef.current === audio) {
          audioRef.current = null;
        }
      };
    }
  }, [audioUrl]);

  const resetPlaybackState = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    //setDuration(0);

    if (progressTimerRef.current) {
      cancelAnimationFrame(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const togglePlayback = () => {
    if (!audioRef.current) {
      const audio = new Audio(audioUrl);
      audio.preload = "metadata";

      const handleMetadata = () => {
        const audioDuration = audio.duration;
        if (audioDuration && isFinite(audioDuration)) {
          setDuration(audioDuration);
        }
      };

      audio.addEventListener("loadedmetadata", handleMetadata, { once: true });
      audio.addEventListener("ended", resetPlaybackState, { once: true });

      audio.load();
      audioRef.current = audio;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPaused(true);
      resetPlaybackState();
    } else {
      audioManager.current.setCurrentAudio(
        audioRef.current,
        resetPlaybackState,
      );
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsPaused(false);
          updateProgress();
        })
        .catch((error) => {
          console.error("Error playing audio:", error);
          resetPlaybackState();
        });
    }
  };

  // Audio playback ended, resetting state
  useEffect(() => {
    if (audioRef.current) {
      const handleEnded = () => {
        resetPlaybackState();
      };

      audioRef.current.addEventListener("ended", handleEnded);

      return () => {
        audioRef.current?.removeEventListener("ended", handleEnded);
      };
    }
  }, [audioRef.current]); // Runs when audioRef changes

  const updateProgress = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime || 0;
      const audioDuration = audioRef.current.duration;

      if (audioDuration && isFinite(audioDuration)) {
        setProgress(currentTime / audioDuration);
        setDuration(audioDuration); // Keep duration updated
      }

      progressTimerRef.current = requestAnimationFrame(updateProgress);
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      stopTimer();
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (progressTimerRef.current) {
        cancelAnimationFrame(progressTimerRef.current);
      }
      audioManager.current.stopCurrent(resetPlaybackState);
    };
  }, [resetPlaybackState]);

  // For displaying the recorded voice message preview and already recored voice
  if (audioUrl) {
    return isNewVoiceChat ? (
      <Box>
        <HStack alignItems="center" space={3}>
          <Pressable
            onPress={togglePlayback}
            bg="AVColor.primary"
            p={2}
            borderRadius="full"
          >
            {isPlaying ? (
              <PauseIcon size="20px" color="white" />
            ) : (
              <PlayIcon size="20px" color="white" />
            )}
          </Pressable>

          <HStack space={0.5} alignItems="flex-end" flex={1}>
            {waveformBars.map((height, i) => (
              <Animated.View
                key={i}
                style={[
                  styles.waveformBar,
                  {
                    height,
                    backgroundColor:
                      i / 30 < progress || isPaused ? "#32AE78" : "#E5E5E5",
                    transform: [{ scaleY: barScales[i] }],
                  },
                ]}
              />
            ))}
          </HStack>

          <Text fontSize="xs" color="gray.600">
            {isPlaying
              ? formatTime(audioRef.current?.currentTime || 0)
              : formatTime(duration)}
          </Text>
        </HStack>
      </Box>
    ) : (
      <HStack
        space={2}
        alignItems="center"
        bg="white"
        p={3}
        borderRadius="xl"
        w="192px"
        shadow={2}
      >
        <Pressable
          onPress={togglePlayback}
          bg="AVColor.primary"
          p={2}
          borderRadius="full"
        >
          {isPlaying ? (
            <PauseIcon size="20px" color="white" />
          ) : (
            <PlayIcon size="20px" color="white" />
          )}
        </Pressable>

        <VStack flex={1} space={1}>
          <HStack space={0.5} alignItems="center" h="16px">
            {Array.from({ length: 30 }).map((_, i) => (
              <Animated.View
                key={i}
                style={{
                  width: 2,
                  height: isPlaying || isPaused ? waveformBars[i] : 4,
                  backgroundColor:
                    i / 30 < progress || isPaused ? "#32AE78" : "#E5E5E5",
                  borderRadius: 999,
                  transform: [{ scaleY: barScales[i] }],
                }}
              />
            ))}
          </HStack>

          <HStack justifyContent="flex-end" alignItems="center">
            <Text fontSize="xs" color="gray.500">
              {isPlaying
                ? formatTime(audioRef.current?.currentTime || 0)
                : formatTime(duration)}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    );
  }

  // For recording audio
  if (isNewVoiceChat) {
    return (
      <Box>
        <HStack alignItems="center" space={3}>
          <HStack space={0.5} alignItems="flex-end" flex={1}>
            {waveformBars.map((height, i) => (
              <Animated.View
                key={i}
                style={[
                  styles.waveformBar,
                  {
                    height,
                    backgroundColor: "#32AE78",
                    transform: [{ scaleY: barScales[i] }],
                  },
                ]}
              />
            ))}
          </HStack>

          <Text fontSize="xs" color="gray.600">
            {formatTime(recordingAudioTime || 0)}
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <HStack space={0} alignItems="center" marginTop="0px">
      <Button
        onPress={isRecording ? stopRecording : startRecording}
        bg="transparent"
        width="20px"
        height="20px"
        minWidth="20px"
        padding={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{
          bg: "transparent",
        }}
        isDisabled={!isOnline || isDisabled}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="full"
          height="full"
        >
          {isRecording ? (
            <StopIcon size="24px" color="red.500" />
          ) : (
            <MicIcon size="24px" color="AVColor.primary" />
          )}
        </Box>
      </Button>
    </HStack>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 10,
    alignItems: "center",
  },
  waveformContainer: {
    // flex: 1,
    flexDirection: "row",
    alignItems: "center",
    height: 30,
    marginHorizontal: 8,
    gap: 5,
  },
  waveformBar: {
    width: 2.5,
    height: "100%",
    borderRadius: 2,
    backgroundColor: "#32AE78",
  },
});

export default forwardRef(VoiceMessage);
